import React from "react";
import { FormControl } from "@remo-co/ui-core/src/components/FormControl";
import { FormHelperText } from "@remo-co/ui-core/src/components/FormHelperText";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import FeatureToggle from "modules/manageEvent/components/FeatureToggle";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import logger from "logging/logger";
import {
  CompanyFeatureRecord,
  CompanyFeatureToggle,
} from "modules/company/types";
import { CompanyFeatureToggles } from "modules/company/constants";
import useCompany from "../company/hooks/useCompany";
import companyApi from "../company/company.api";
import { selectCompanySettingsFeatureToggles } from "../companySettings/redux/selectors";
import { styles } from "./CompanyFeatureTogglesStyles";

const CompanyFeatureTogglesComponent = () => {
  const { addSuccessNotification, addErrorNotification } =
    useNotificationActions();
  const { t } = useI18n(["common", "settings", "manageEvent", "polls"]);
  const { company } = useCompany();
  const featureToggles: CompanyFeatureToggles[] = [
    CompanyFeatureToggles.Chat,
    CompanyFeatureToggles.ChatUpload,
    CompanyFeatureToggles.Polls,
    CompanyFeatureToggles.LockTables,
    CompanyFeatureToggles.QA,
    CompanyFeatureToggles.Whiteboard,
  ];
  const disabledFeatures = useSelector(selectCompanySettingsFeatureToggles);
  const [featureTogglesRecord, setFeatureTogglesRecord] =
    React.useState<CompanyFeatureRecord>({
      [CompanyFeatureToggles.Chat]: true,
      [CompanyFeatureToggles.ChatUpload]: true,
      [CompanyFeatureToggles.Polls]: true,
      [CompanyFeatureToggles.LockTables]: true,
      [CompanyFeatureToggles.QA]: true,
      [CompanyFeatureToggles.Whiteboard]: true,
    });

  const getLabelTextFromFeatureName = (feature: CompanyFeatureToggles) => {
    switch (feature) {
      case CompanyFeatureToggles.Chat:
        return t("chat.button");
      case CompanyFeatureToggles.ChatUpload:
        return t("upload.to.chat");
      case CompanyFeatureToggles.QA:
        return t("q&a");
      case CompanyFeatureToggles.Whiteboard:
        return t("button.whiteboard");
      case CompanyFeatureToggles.Polls:
        return t("polls:label");
      case CompanyFeatureToggles.LockTables:
        return t("manageEvent:lock.tables.by.guests");
      default:
        return "";
    }
  };

  React.useEffect(() => {
    const initialDisabledToggles = disabledFeatures?.reduce(
      (acc: CompanyFeatureRecord, feat: CompanyFeatureToggle) => ({
        ...acc,
        [feat]: false,
      }), // "uncheck" feature toggles already disabled in company settings
      {},
    );

    setFeatureTogglesRecord({
      ...featureTogglesRecord,
      ...initialDisabledToggles,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureToggles.length, disabledFeatures.length]);

  if (!company?.id) return null;

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = event.target;

    try {
      const updatedRecord = {
        ...featureTogglesRecord,
        [name]: checked,
      };

      setFeatureTogglesRecord(updatedRecord);
      const featuresToDisable = Object.keys(updatedRecord).filter(
        (feat: string) => !updatedRecord[feat as CompanyFeatureToggle],
      );

      await companyApi.updateDisabledFeatureToggles(
        company.id,
        featuresToDisable as CompanyFeatureToggle[],
      );

      addSuccessNotification({
        message: t("settings:company.feature.toggles.success", {
          feature: getLabelTextFromFeatureName(name as CompanyFeatureToggles),
        }),
      });
    } catch (error) {
      if (error instanceof Error) {
        setFeatureTogglesRecord({
          ...featureTogglesRecord,
          [name]: !checked,
        });
        addErrorNotification({
          message: t("settings:company.feature.toggles.failure", {
            feature: getLabelTextFromFeatureName(name as CompanyFeatureToggles),
          }),
        });
        logger.error(
          "[CompanyFeatureToggles][handleChange] Failed to update company feature toggle",
          { name, message: error.message },
        );
      }
    }
  };

  return (
    <div data-testid="company-feat-toggles-ctn">
      <Typography variant="h3" sx={styles.headerText}>
        {t("settings:company.feature.toggles.header")}
      </Typography>
      <FormControl component="fieldset">
        <FormHelperText sx={styles.text}>
          <Typography variant="h5" color="textPrimary">
            {t("settings:company.feature.toggles.text")}
          </Typography>
        </FormHelperText>
        {featureToggles.map((feature) => (
          <FeatureToggle
            label={getLabelTextFromFeatureName(feature)}
            checked={featureTogglesRecord[feature] === true}
            name={feature}
            onToggle={handleChange}
            key={feature}
            testId={getLabelTextFromFeatureName(feature)}
          />
        ))}
      </FormControl>
    </div>
  );
};

export default CompanyFeatureTogglesComponent;
